/**
 * Downscale and re-encode a camera photo to JPEG before it is stored for upload. Phone cameras
 * produce 3–8 MB images; at the gate we need legible slips and bag photos, not prints.
 */
export interface CompressOptions {
  maxEdge?: number
  quality?: number
  /**
   * Never keep a photo smaller than this: below it the numbers get hard to read. If the first
   * encode is smaller, it is encoded again larger/sharper (STEP_UP) until it reaches this or the
   * last step.
   */
  minBytes?: number
}

/** Tried in turn when a photo came out under `minBytes`. The last step is the full setting. */
const STEP_UP: ReadonlyArray<{ maxEdge: number; quality: number }> = [
  { maxEdge: 1600, quality: 0.72 },
  { maxEdge: 1600, quality: 0.85 },
]

const KB = 1024

/**
 * How big each kind of photo is kept. Most gate photos only prove a number (a scale or moisture
 * reading, a tag, a seal), so they are kept smaller than a face, but never under 150 KB: below
 * that the office finds the numbers hard to read.
 *  - proof    : scale, moisture meter, bags, tags, seals, labels
 *  - document : a slip or a paper page with handwriting
 *  - person   : a farmer's or deliverer's face
 */
export type PhotoKind = 'proof' | 'document' | 'person'
export const PHOTO_SIZES: Record<PhotoKind, CompressOptions> = {
  proof: { maxEdge: 1280, quality: 0.62, minBytes: 150 * KB },
  document: { maxEdge: 1600, quality: 0.72, minBytes: 150 * KB },
  person: { maxEdge: 1600, quality: 0.72 },
}

export async function compressImage(file: Blob, opts: CompressOptions = {}): Promise<Blob> {
  const { maxEdge = 1600, quality = 0.72, minBytes = 0 } = opts
  const bitmap = await loadBitmap(file)
  try {
    let blob = await encode(bitmap, maxEdge, quality)
    for (const step of STEP_UP) {
      if (blob.size >= minBytes) break
      if (step.maxEdge < maxEdge || (step.maxEdge === maxEdge && step.quality <= quality)) continue
      blob = await encode(bitmap, step.maxEdge, step.quality)
    }
    return blob
  } finally {
    if ('close' in bitmap) bitmap.close()
  }
}

async function encode(
  bitmap: ImageBitmap | HTMLImageElement,
  maxEdge: number,
  quality: number,
): Promise<Blob> {
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height))
  const w = Math.max(1, Math.round(bitmap.width * scale))
  const h = Math.max(1, Math.round(bitmap.height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const g = canvas.getContext('2d')
  if (!g) throw new Error('Canvas not available')
  g.drawImage(bitmap, 0, 0, w, h)

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality),
  )
  if (!blob) throw new Error('Could not encode the photo')
  return blob
}

async function loadBitmap(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    try {
      // Honour EXIF orientation so portrait photos are not stored sideways.
      return await createImageBitmap(file, { imageOrientation: 'from-image' })
    } catch {
      /* fall through to <img> decode */
    }
  }
  const url = URL.createObjectURL(file)
  try {
    const img = new Image()
    img.decoding = 'async'
    img.src = url
    await img.decode()
    return img
  } finally {
    URL.revokeObjectURL(url)
  }
}
